import React, { useState, useEffect, useCallback } from 'react';
import { notesService } from '../services/api';
import './NoteForm.css';
import { useAuth } from '../contexts/AuthContext';

const NoteForm = ({ entityId, entityType, onNoteAdded }) => {
  const [noteText, setNoteText] = useState('');
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { isAdmin } = useAuth();

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await notesService.list(entityType, entityId);
      setNotes(response.data.data || []);
    } catch (err) {
      setError('Failed to load notes');
      console.error('Error fetching notes:', err);
    } finally {
      setLoading(false);
    }
  }, [entityId, entityType]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) {
      setError('Note cannot be empty');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await notesService.create(entityType, entityId, { note_text: noteText });
      setSuccess('Note added successfully');
      setNoteText('');
      fetchNotes();
      if (onNoteAdded) onNoteAdded();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add note');
      console.error('Error adding note:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;

    setLoading(true);
    setError('');

    try {
      await notesService.delete(noteId);
      setSuccess('Note deleted successfully');
      fetchNotes();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete note');
      console.error('Error deleting note:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="note-form-container">
      <h3>Notes</h3>

      {/* Add Note Form */}
      <form onSubmit={handleAddNote} className="note-input-form">
        <textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Add a note..."
          rows="3"
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || !noteText.trim()}
          className="btn btn-primary"
        >
          {loading ? 'Adding...' : 'Add Note'}
        </button>
      </form>

      {/* Messages */}
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Notes List */}
      <div className="notes-list">
        {notes.length === 0 ? (
          <p className="no-notes">No notes yet</p>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="note-item">
              <div className="note-content">
                <p>{note.note_text}</p>
                <span className="note-meta">
                  {note.created_by_name && `by ${note.created_by_name}`}
                  {note.created_at && ` on ${new Date(note.created_at).toLocaleDateString()}`}
                </span>
              </div>
              {isAdmin && <button
                onClick={() => handleDeleteNote(note.id)}
                className="btn-delete-note"
                title="Delete note"
              >
                ✕
              </button>}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default NoteForm;
